// =====================================================================
// The sound the "still there?" prompt makes.
//
// Synthesised rather than a file: no asset to fail to load, and a prompt nobody hears
// is a prompt that costs a working SDR their seat.
//
// Two short notes, quiet, a fifth apart — audible across a room without reading as an
// alarm, since most of the people who hear it are simply looking at another window.
// =====================================================================

const NOTES_HZ = [660, 990];
const NOTE_MS = 140;
const PEAK_GAIN = 0.12;

// One context for the life of the tab. Browsers cap how many a page may hold, and a
// prompt can recur all shift.
let shared: AudioContext | null = null;

export function playPresenceChime(): void {
  try {
    shared ??= new AudioContext();
  } catch {
    return;
  }

  const ctx = shared;
  if (ctx.state === 'running') {
    emit(ctx);
    return;
  }

  // A tab with no prior gesture holds a suspended context — which is exactly the away
  // case this exists for. Resuming is asynchronous, so the sound has to wait for it;
  // testing the state on the next line would drop every chime that mattered.
  void ctx
    .resume()
    .then(() => {
      if (ctx.state === 'running') emit(ctx);
    })
    .catch(() => {
      // Autoplay policy said no. Silence is the documented fallback — the prompt on
      // screen is the mechanism, the sound is only what draws an eye to it.
    });
}

function emit(ctx: AudioContext): void {
  NOTES_HZ.forEach((hz, i) => {
    const startAt = ctx.currentTime + (i * NOTE_MS) / 1000;
    const endAt = startAt + NOTE_MS / 1000;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = hz;

    // Ramped rather than switched: an instant start or stop on a sine is a click, and
    // a click is what makes a quiet sound feel like an error.
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.02);
    gain.gain.linearRampToValueAtTime(0, endAt);

    osc.connect(gain).connect(ctx.destination);
    osc.start(startAt);
    osc.stop(endAt + 0.02);
  });
}
